import { useEffect, useRef, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'

export interface UseCountUpOptions {
  duration?: number
  initialValue?: number
}

const DEFAULT_DURATION = 600

const normalizeValue = (value: number): number => {
  if (!Number.isFinite(value) || value < 0) {
    return 0
  }

  return value
}

const useCountUp = (value: number, options: UseCountUpOptions = {}): number => {
  const duration = Math.max(0, options.duration ?? DEFAULT_DURATION)
  const normalizedInitialValue = normalizeValue(options.initialValue ?? value)
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
    defaultMatches: false,
    noSsr: true,
  })
  const [displayValue, setDisplayValue] = useState<number>(() => normalizedInitialValue)
  const latestValueRef = useRef<number>(normalizedInitialValue)
  const frameRef = useRef<number | null>(null)
  const hasExplicitInitialValue = options.initialValue !== undefined

  useEffect(() => {
    latestValueRef.current = displayValue
  }, [displayValue])

  useEffect(() => {
    if (!hasExplicitInitialValue) {
      return
    }

    latestValueRef.current = normalizedInitialValue
    setDisplayValue(normalizedInitialValue)
  }, [hasExplicitInitialValue, normalizedInitialValue])

  useEffect(() => {
    const target = normalizeValue(value)
    const hasWindow = typeof window !== 'undefined'
    const requestFrame = hasWindow ? window.requestAnimationFrame : null
    const cancelFrame = hasWindow ? window.cancelAnimationFrame : null

    if (prefersReducedMotion || duration === 0 || !requestFrame || !cancelFrame) {
      if (frameRef.current !== null) {
        cancelFrame?.(frameRef.current)
        frameRef.current = null
      }
      latestValueRef.current = target
      setDisplayValue(target)
      return
    }

    const startValue = latestValueRef.current

    if (frameRef.current !== null) {
      cancelFrame(frameRef.current)
      frameRef.current = null
    }

    if (startValue === target) {
      return
    }

    const change = target - startValue
    let startTime: number | null = null

    const step = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp
      }

      const elapsed = timestamp - startTime
      const progress = duration > 0 ? Math.min(1, elapsed / duration) : 1
      const eased = 1 - Math.pow(1 - progress, 3)
      const next = startValue + change * eased

      latestValueRef.current = next
      setDisplayValue(next)

      if (progress < 1) {
        frameRef.current = requestFrame(step)
      } else {
        frameRef.current = null
        latestValueRef.current = target
        setDisplayValue(target)
      }
    }

    frameRef.current = requestFrame(step)

    return () => {
      if (frameRef.current !== null) {
        cancelFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [value, duration, prefersReducedMotion, normalizedInitialValue])

  return displayValue
}

export default useCountUp
